import { z } from 'zod';

/**
 * Normalização de telefone para E.164 (Brasil), feita **uma única vez**, na
 * entrada da API (CLAUDE.md, regra 4). Nunca normalizar no frontend, no agente
 * ou na query.
 *
 * Aceita as formas que aparecem na prática:
 *   "11999998888", "(11) 99999-8888", "+55 11 99999-8888",
 *   "5511999998888", "011999998888"
 * Devolve sempre "+55DDDNNNNNNNN" (12 ou 13 caracteres após o "+55").
 */

export class TelefoneInvalidoError extends Error {
  readonly codigo = 'telefone_invalido' as const;
  constructor(readonly valorOriginal: string) {
    super(`Telefone inválido: ${JSON.stringify(valorOriginal)}`);
    this.name = 'TelefoneInvalidoError';
  }
}

/** DDDs válidos no Brasil (todos terminam em 1..9; o 0 nunca é segundo dígito). */
const DDD_REGEX = /^[1-9][1-9]$/;

export function normalizarTelefoneE164(entrada: string): string {
  const original = entrada;
  let digitos = entrada.replace(/\D/g, '');

  // Prefixo internacional "00" digitado à mão.
  digitos = digitos.replace(/^0+(?=\d{10,})/, (m) => (m.length >= 2 ? '' : m));

  // Código do país explícito.
  if (digitos.startsWith('55') && (digitos.length === 12 || digitos.length === 13)) {
    digitos = digitos.slice(2);
  }

  // Prefixo de tronco (0 + DDD + número).
  if ((digitos.length === 11 || digitos.length === 12) && digitos.startsWith('0')) {
    digitos = digitos.slice(1);
  }

  // Agora o esperado é o número nacional: 10 (fixo) ou 11 (celular) dígitos.
  if (digitos.length !== 10 && digitos.length !== 11) {
    throw new TelefoneInvalidoError(original);
  }

  const ddd = digitos.slice(0, 2);
  if (!DDD_REGEX.test(ddd)) {
    throw new TelefoneInvalidoError(original);
  }

  // Celular tem 11 dígitos e o nono dígito é 9. Fixo tem 10.
  if (digitos.length === 11 && digitos[2] !== '9') {
    throw new TelefoneInvalidoError(original);
  }

  return `+55${digitos}`;
}

export function ehTelefoneE164(valor: string): boolean {
  return /^\+55[1-9][1-9]\d{8,9}$/.test(valor);
}

/**
 * Formato para `wa.me`: E.164 sem o "+" (SPEC.md §7).
 * `+5511999998888` -> `5511999998888`
 */
export function telefoneParaWaMe(e164: string): string {
  return e164.replace(/^\+/, '');
}

/** Zod: aceita string livre e devolve o E.164 normalizado. Use na fronteira da API. */
export const telefoneSchema = z
  .string()
  .trim()
  .min(1, 'Telefone é obrigatório')
  .transform((v, ctx) => {
    try {
      return normalizarTelefoneE164(v);
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Telefone inválido' });
      return z.NEVER;
    }
  });

/** Zod: exige que o valor já esteja em E.164 (uso interno, pós-normalização). */
export const telefoneE164Schema = z.string().refine(ehTelefoneE164, 'Telefone não está em E.164');
